import React, { useRef, useEffect, useState, useCallback } from 'react';
import { InputGroup, FormControl } from 'react-bootstrap';

const useForceUpdate = () => {
  const [tick, setTick] = useState(0); // State variable to trigger re-renders

  const update = useCallback(() => {
    setTick((tick) => tick + 1);
  }, []); // Memoized callback that increments 'tick' to force a re-render

  return [update, tick]; // Returns the update function and current tick value for dependency tracking
}; // This custom hook is created to provide a method to force a React component to re-render (in this case adding leading zeros to the number input field like 03 does not change the value and therefor also doesn't trigger the useEffect hook)

const CustomInput = ({
  className,
  position = 'top',
  value,
  onChange,
  onBlur,
  onKeyDown,
  onWheel,
  unitSymbol = '',
  step,
  min,
  max,
  disabled,
}) => {
  const inputRef = useRef(null);
  const symbolRef = useRef(null);
  const [forceUpdate, tick] = useForceUpdate(); // Retrieves the forceUpdate function and tick counter from the custom hook for manual re-renders.

  const [size, setSize] = useState(window.innerWidth); // State to track window size (so the useEffect hook gets triggered, whenever the user changes the size of the browser. Because the width of the input field may change as well and the position of the symbol must be recalculated)

  const updateSize = () => {
    setSize(window.innerWidth); // Update size state on window resize
  };

  useEffect(() => {
    window.addEventListener('resize', updateSize); // Add resize event listener
    return () => window.removeEventListener('resize', updateSize); // Clean up listener on component unmount
  }, []);

  function getTextWidth(text, font) {
    const canvas = document.createElement('canvas'); // create a canvas element (off-DOM)
    const context = canvas.getContext('2d'); // get the 2D drawing context
    context.font = font; // apply the font to the context for accurate measurement
    return context.measureText(text).width; // measure and return the width of the text
  } // utility function to calculate the width of a given text string with a specific font

  useEffect(() => {
    if (inputRef.current && symbolRef.current && unitSymbol) {
      // If both input and symbol elements are present and if a unit symbol is defined:
      const input = inputRef.current;
      const symbol = symbolRef.current;

      const computedStyle = getComputedStyle(input); // get the css styles from the input
      const currentValue = input.value; // get the current string inside the input

      const font = computedStyle.font; // extract font for accurate measurement

      let textWidth = getTextWidth(currentValue, font); // measure the unscaled width of the input text

      // Read and parse the transform matrix to determine applied scale (the customizer-mobile-device-compatible-top css class comes with scale applied):
      const transform = computedStyle.transform;
      let scale = 1;

      if (transform && transform.startsWith('matrix')) {
        const values = transform.match(/matrix\(([^)]+)\)/); // extract values inside matrix()

        if (values && values[1]) {
          const parts = values[1].split(',').map(parseFloat); // convert to array of numbers
          if (parts.length >= 1 && !isNaN(parts[0])) {
            scale = parts[0]; // use scaleX value (1st value in matrix)
          }
        }
      }

      textWidth *= scale; // apply the scale factor

      symbol.style.left = `${textWidth + 2}px`; // position the unit symbol (e.g., °) right after the end of the visible text
    }
  }, [value, unitSymbol, tick, size]); // this useEffect postitions the unitSymbol behind the input string

  const handleBlur = onBlur ? (e) => onBlur(e) : undefined; // Handle onBlur only if provided
  const handleKeyDown = onKeyDown ? (e) => onKeyDown(e) : undefined; // Handle onKeyDown only if provided

  return (
    <InputGroup
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <FormControl
        className={
          position === 'top'
            ? 'customizer-mobile-device-compatible-top'
            : position === 'center'
              ? 'customizer-mobile-device-compatible-center'
              : ''
        }
        style={{
          zIndex: 1,
        }}
        ref={inputRef}
        {...(className && { className })} // Conditionally apply className if provided
        type="number"
        value={value === '__MIXED__' ? '' : value}
        onChange={(e) => {
          onChange(e);
          forceUpdate(); // This will trigger the useEffect even if the value hasn't 'changed'. For example when adding zeros in the front of the number (3 -> 03). The value didn't change and therefor wouldn't trigger the useEffect to calculate the new width of the value inside the input field. But this is needed, as the "°" symbol needs to be adjusted for the larger width of 03 compared to just 0
        }}
        onBlur={handleBlur} // Execute onBlur if provided, otherwise undefined
        onKeyDown={handleKeyDown} // Execute onKeyDown if provided, otherwise undefined
        onWheel={(e) => {
          e.target.blur(); // Unfocus the input to prevent scrolling from changing the value
          setTimeout(() => e.target.focus(), 0); // Refocus it after the wheel event
        }}
        {...(step && { step })} // Conditionally apply step if provided
        {...(min !== undefined && { min })} // Conditionally apply min if provided
        {...(max !== undefined && { max })} // Conditionally apply max if provided
        {...(disabled !== undefined && { disabled })} // Conditionally apply disabled if provided
      />

      {unitSymbol && value !== '__MIXED__' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'hidden', // Hide only the unit symbol
            width: 'calc(100% - 17px)',
            ...(position === 'center' && {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }), // also center the unit symbol, when the position of the CustomInput is set to 'center'
          }}
        >
          <InputGroup.Text
            ref={symbolRef}
            style={{
              position: 'relative',
              top: unitSymbol === '°' ? '-2px' : '0px',
              transform: 'translateY(3%)',
              zIndex: 1,
              pointerEvents: 'none', // Makes the label unselectable
              backgroundColor: 'transparent',
              border: 'none',
              opacity: disabled ? '0.2' : '0.5',
            }}
          >
            {unitSymbol}
          </InputGroup.Text>
        </div>
      )}
    </InputGroup>
  );
};

export default CustomInput;
